import {
  useMutation,
  UseMutationOptions,
  UseMutationResult,
  useQueryClient,
} from '@tanstack/react-query';

import APIError from 'types/api_error';
import { Organization } from 'types/organization';

import { useAPI } from 'hooks/api/useAPI';

import { useAuthenticatedQuery } from './useAuthenticatedQuery';

export function useOrganizations() {
  const { client } = useAPI();

  return useAuthenticatedQuery(
    ['organizations'],
    async () => {
      const { data } = await client.get<Organization[]>('/internal/orgs');
      return data;
    },
    {
      staleTime: Infinity,
      select(orgs) {
        return orgs.sort((a, b) =>
          a.name.toUpperCase() > b.name.toUpperCase() ? 1 : -1
        );
      },
    }
  );
}

export function useUpdateOrganization({
  onSuccess,
  ...options
}: UseMutationOptions<
  Organization,
  APIError,
  Organization
> = {}): UseMutationResult<Organization, APIError, Organization> {
  const { client } = useAPI();
  const queryClient = useQueryClient();

  return useMutation({
    ...options,
    async mutationFn({ id, name }) {
      const { data } = await client.put(`/orgs/${id}`, { name });
      return data;
    },
    onSuccess(...args) {
      onSuccess?.(...args);
      queryClient.invalidateQueries(['organizations']);
    },
  });
}
